
import React, { useState, useEffect } from 'react';
import { CaseFile, LegalPayment, UserData } from '../types';
import { analyzePerformance } from '../services/geminiService';
import { RateLimitService } from '../services/rateLimitService';
import { toast } from 'sonner';
import CaseModal from './CaseModal';

interface PerformanceViewProps {
  cases: CaseFile[];
  payments: LegalPayment[];
  user: UserData;
  onAddCase: (c: Partial<CaseFile>) => void;
  onUpdateCase: (id: string, updates: Partial<CaseFile>) => void;
}

const PerformanceView: React.FC<PerformanceViewProps> = ({ cases, payments, user, onAddCase, onUpdateCase }) => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');
  const [aiVerdict, setAiVerdict] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<CaseFile | null>(null);

  const handleOpenAddCase = () => {
    setEditingCase(null);
    setIsCaseModalOpen(true);
  };

  const handleOpenEditCase = (c: CaseFile) => {
    setEditingCase(c);
    setIsCaseModalOpen(true);
  };

  const handleSaveCase = (caseData: Partial<CaseFile>) => {
    if (editingCase) {
      onUpdateCase(editingCase.id, caseData);
      toast.success("Dosar actualizat cu succes!");
    } else {
      onAddCase(caseData);
      toast.success("Dosar adăugat cu succes!");
    }
  };

  const getFilteredPayments = () => {
    const now = new Date();
    return payments.filter(p => {
      const pDate = new Date(p.dueDate);
      if (timeRange === 'week') return pDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      if (timeRange === 'month') return pDate.getMonth() === now.getMonth() && pDate.getFullYear() === now.getFullYear();
      return pDate.getFullYear() === now.getFullYear();
    });
  };

  const currentPayments = getFilteredPayments();
  const revenue = currentPayments.filter(p => p.direction === 'incasare' && p.status === 'completed').reduce((acc, p) => acc + p.amount, 0);
  const activeCases = cases.filter(c => c.status === 'În lucru').length;
  const closedCases = cases.filter(c => c.status.startsWith('Închis'));
  const wonCases = cases.filter(c => c.status === 'Închis - Câștigat').length;
  const winRate = closedCases.length > 0 ? Math.round((wonCases / closedCases.length) * 100) : 0;

  useEffect(() => {
    const fetchVerdict = async () => {
      if (!RateLimitService.checkDailyLimit('aiCalls')) {
        setAiVerdict(`Analiza AI indisponibilă: Limita de protecție zilnică atinsă.`);
        return;
      }

      setAiVerdict('AI analizează datele financiare...');
      try {
        RateLimitService.incrementUsage('aiCalls');
        const verdict = await analyzePerformance(cases, currentPayments);
        setAiVerdict(verdict);
      } catch (err: any) {
        setAiVerdict("A apărut o eroare la generarea analizei. Vă rugăm reîncercați.");
      }
    };
    fetchVerdict();
  }, [timeRange, cases, payments]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight italic leading-tight">Performanță Cabinet</h2>
          <p className="text-slate-400 font-bold uppercase text-[8px] md:text-[10px] tracking-widest mt-1 italic">Vedere: {timeRange === 'week' ? 'Săptămână' : timeRange === 'month' ? 'Lună' : 'Anual'}</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-xl md:rounded-2xl border border-slate-200 w-full md:w-auto">
          {(['week', 'month', 'year'] as const).map(range => (
            <button 
              key={range} 
              onClick={() => setTimeRange(range)} 
              title={`Filtrează după: ${range === 'week' ? 'Săptămână' : range === 'month' ? 'Lună' : 'An'}`}
              aria-label={`Filtrează după: ${range === 'week' ? 'Săptămână' : range === 'month' ? 'Lună' : 'An'}`}
              className={`flex-grow md:flex-none px-4 md:px-6 py-2 rounded-lg md:rounded-xl text-[9px] md:text-xs font-black uppercase tracking-widest transition-all ${timeRange === range ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
            >
              {range === 'week' ? 'Săpt.' : range === 'month' ? 'Lună' : 'An'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {[
          { label: 'Profit', val: `${revenue.toLocaleString()} RON`, color: 'text-emerald-600', icon: '💰' },
          { label: 'Succes', val: `${winRate}%`, color: 'text-indigo-600', icon: '⚖️' },
          { label: 'Active', val: activeCases, color: 'text-slate-900', icon: '📂' },
          { label: 'AI Score', val: '84%', color: 'text-orange-600', icon: '⏱️' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white p-4 md:p-8 rounded-2xl md:rounded-[2.5rem] border border-slate-100 shadow-xl">
            <span className="text-xl md:text-2xl mb-2 block">{kpi.icon}</span>
            <p className="text-[8px] md:text-xs font-black text-slate-400 uppercase tracking-widest mb-1">{kpi.label}</p>
            <p className={`text-xl md:text-3xl font-black tracking-tighter ${kpi.color}`}>{kpi.val}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2 bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 shadow-xl overflow-hidden">
          <div className="p-6 md:p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/20">
            <h3 className="text-base md:text-lg font-black text-slate-900 italic tracking-tight uppercase">Activitate Dosare</h3>
            <button
              onClick={handleOpenAddCase}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 md:px-6 py-2 md:py-3 rounded-xl font-black text-[9px] md:text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-200 transition-all active:scale-95"
            >
              + Adaugă Dosar
            </button>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-slate-50/50"><th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Dosar</th><th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Fază Procesuală</th><th className="px-6 py-4 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest">Eficiență</th><th className="px-6 py-4 text-center text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {cases.map(c => (
                  <tr key={c.id} onClick={() => handleOpenEditCase(c)} className="hover:bg-slate-50/30 transition-colors cursor-pointer group">
                    <td className="px-8 py-6"><p className="font-black text-slate-900 text-sm italic group-hover:text-indigo-600 transition-colors">{c.caseNumber}</p><p className="text-[9px] text-slate-400 font-bold">{c.clientName}</p></td>
                    <td className="px-6 py-6"><span className="text-[9px] font-black px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 uppercase">{c.phase || 'Fond'}</span></td>
                    <td className="px-6 py-6 text-center"><span className="text-[10px] font-black text-slate-600">{c.actualHours}h</span></td>
                    <td className="px-6 py-6 text-center"><span className="text-[9px] bg-slate-900 text-white px-3 py-1.5 rounded-xl font-black uppercase">{c.status.includes('Închis') ? 'Gata' : 'Activ'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden p-4 space-y-3">
             {cases.map(c => (
               <div key={c.id} onClick={() => handleOpenEditCase(c)} className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col gap-3 cursor-pointer active:scale-[0.98] transition-all">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-black text-slate-900 text-xs italic">{c.caseNumber}</p>
                      <p className="text-[8px] text-slate-400 font-bold uppercase">{c.clientName}</p>
                    </div>
                    <span className="text-[9px] bg-white border border-slate-200 px-3 py-1.5 rounded-lg font-black uppercase">{c.actualHours}h</span>
                  </div>
                  <span className="self-start text-[8px] font-black px-2 py-1 rounded bg-indigo-50 text-indigo-600 uppercase">{c.phase || 'Fond'}</span>
               </div>
             ))}
          </div>
        </div>
        
        <div className="bg-slate-900 text-white rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-10 shadow-2xl relative overflow-hidden">
           <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-xl flex items-center justify-center text-xl md:text-2xl">✨</div>
                <div>
                  <h4 className="text-lg md:text-xl font-black tracking-tight italic">Verdict AI</h4>
                  <p className="text-[8px] md:text-[10px] text-indigo-300 font-black uppercase tracking-widest">Recomandări</p>
                </div>
              </div>
              <p className="text-xs md:text-sm font-medium leading-relaxed italic text-indigo-50 border-l-2 border-indigo-500 pl-4 md:pl-6">
                {isAnalyzing ? "Analiză în curs..." : aiVerdict}
              </p>
           </div>
           <div className="absolute -right-20 -bottom-20 w-48 md:w-64 h-48 md:h-64 bg-indigo-500/10 rounded-full blur-[80px]"></div>
        </div>
      </div>
      
      <CaseModal
        isOpen={isCaseModalOpen}
        onClose={() => setIsCaseModalOpen(false)}
        onSave={handleSaveCase}
        initialData={editingCase}
      />
    </div>
  );
};

export default PerformanceView;
